/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';

import template from './line-item-bulk-action-modal.component.html';
import {lineItemCommons} from './line-item-commons';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {compareById} from '../common/util/object-id-comparison';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

@Component({
  standalone: false,
  selector: 'adn-line-item-bulk-action-modal',
  template
})
export class LineItemBulkActionModalComponent implements OnInit {
  list: any;

  item: any = {
    name: '{currentName}',
    objectives: {},
    bidSpecification: {},
    rateLimits: [],
    labelType: 'REPLACE',
    exclusionLabelType: 'REPLACE'
  };
  form: any = {};
  editing: any = {};
  count = 0;
  isMarketplace = false;
  isLineItemTemplate = false;
  isSelfService = false;
  isAxAdvertiser = false;
  isLockedDown = false;
  allowedDeliveryCurves: string[] = [];
  tiers: any[] = [];
  objectiveFields: any = {};
  bidSpecificationFields: any = {};
  rateLimitTypes: string[] = [];

  private commons: any;
  private newEndDate = true;

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.count = _.keys(this.list?.bulkSelection || {}).length;

    const firstSelectionUrl = _.get(_.find(this.list?.bulkSelection || [], () => true) || {}, 'url') || '';
    this.isLineItemTemplate = firstSelectionUrl.indexOf('lineitemtemplate') > -1;
    this.isMarketplace = this.LocalNetworkProfile.isMarketplacePlatform();
    this.tiers = _.cloneDeep(this.LocalNetworkProfile.get('tiers'));

    const pureMarketplacePlatform = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.allowedDeliveryCurves = ['FAST_AS_POSSIBLE', 'EVEN', 'CAPPED', 'FRONT_LOADED', 'CATCH_UP'];
    if (pureMarketplacePlatform) {
      this.allowedDeliveryCurves = ['EVEN', 'CAPPED', 'FRONT_LOADED', 'CATCH_UP'];
    }

    this.commons = lineItemCommons(
      this,
      this.translate,
      this.appLocale,
      this.LocalNetworkProfile,
      this.LocalUserPermissions,
      this.adnTimeShifter,
      'item'
    );
    this.commons.setSelfService();
    this.commons.setObjectivesAsString();
    this.commons.setObjectiveFields();
    this.commons.setBidSpecificationFields();
    this.commons.setAxAdvertiser();
    this.rateLimitTypes = this.rateLimitTypes.length
      ? this.rateLimitTypes
      : _.filter(_.map(this.LocalNetworkProfile.getDefaults().rateLimitFields, (val: boolean, key: string) => {
        return val ? key : false;
      }), (val: any) => val);
  }

  readonly compareById = compareById;

  onEndDateChange(): void {
    if (!this.newEndDate || !this.item.modEndDate) {
      return;
    }
    const networkTimezone = this.LocalNetworkProfile.get('timeZone') || 'utc';
    const defaultEndDayStart = moment(this.item.modEndDate).tz(networkTimezone).startOf('day');
    if (moment(defaultEndDayStart).isSame(this.item.modEndDate)) {
      this.item.modEndDate = moment(this.item.modEndDate).tz(networkTimezone).endOf('day').toDate();
    }
    this.newEndDate = false;
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  apply(): void {
    const lineItems = _.map(this.list.bulkSelection, (v: any, k: string) => {
      const obj: any = {id: k};

      if (this.editing.name) {
        obj.name = this.item.name.replace('{currentName}', v.name);
      }
      if (this.editing.userState && this.item.userState) {
        obj.userState = this.item.userState;
      }
      this.commons.modDates(this.item.modStartDate, this.item.modEndDate);
      if (this.editing.startDate) {
        obj.startDate = this.item.startDate;
      }
      if (this.editing.endDate) {
        obj.endDate = this.item.endDate;
      }
      if (this.editing.objectives) {
        obj.objectives = this.item.objectives;
      }
      if (this.editing.type) {
        obj.type = this.item.type;
      }
      if (this.editing.cpmBid) {
        obj.bidSpecification = obj.bidSpecification || {};
        obj.bidSpecification.cpm = this.item.bidSpecification.cpm;
      }
      if (this.editing.cpcBid) {
        obj.bidSpecification = obj.bidSpecification || {};
        obj.bidSpecification.cpc = this.item.bidSpecification.cpc;
      }
      if (this.editing.smoothed) {
        obj.deliveryCurve = this.item.deliveryCurve;
      }
      if (this.editing.shareOfVoice) {
        obj.sponsorshipPercentage = this.item.sponsorshipPercentage;
      }
      if (this.editing.rateLimits) {
        obj.rateLimits = this.item.rateLimits;
      }
      if (this.editing.companionAds) {
        obj.companionAds = this.item.companionAds;
      }
      if (this.editing.deduplicationLevel) {
        obj.deduplicationLevel = this.item.deduplicationLevel;
      }
      if (this.editing.exclusionLabels) {
        obj.exclusionLabels = this.item.exclusionLabelType === 'APPEND'
          ? (v.exclusionLabels || []).concat(this.item.exclusionLabels || [])
          : (this.item.exclusionLabels || []);
      }
      if (this.editing.labels) {
        obj.labels = this.item.labelType === 'APPEND'
          ? (v.labels || []).concat(this.item.labels || [])
          : (this.item.labels || []);
      }
      if (this.editing.tier && this.item.tier) {
        obj.tier = this.item.tier || null;
      }

      return obj;
    });

    this.list.miscBulkAction(
      {close: () => this.bsModalRef.hide()},
      this,
      lineItems,
      null,
      {ignoreProduct: this.LocalNetworkProfile.getDefaults()['defaultIgnoreMarketplaceTargeting']}
    );
  }
}
